import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter, Input, OnChanges,
  OnDestroy,
  Output, SimpleChanges,
  ViewChild
} from '@angular/core';
import {CommonModule} from "@angular/common";
import {MaterializeAgent} from "../../agent/materializeAgent";
import {interval, Subscription} from "rxjs";
import {
  MaterializePlate, MaterializePlateFrame,
  MaterializePlateStreamingFrame, MaterializePlateSummary
} from "../../../impl/agent/materializeAgentPlate";
import {
  MaterializePlateExamControl,
  PressureGravityCenter,
  PressureLocation
} from "../../../impl/control/materializePlateExam.control";
import {ButtonModule} from "../../../../../core/component/button/button.module";
import {CardModule} from "../../../../../core/component/card/card.module";
import {ErrorTextModule} from "../../../../../core/component/error-text/error-text.module";
import {SelectModule} from "../../../../../../components/common/select/select.module";
import {LoadingModule} from "../../../../../../components/common/loading/loading.module";
import {StickyContainerModule} from "../../../../../core/component/sticky-container/sticky-container.module";
import {RandomUtils} from "../../../../../../shared/utils/random.utils";
import {BaseView} from "../../../../../core/view/base.view";
import {ArrayUtils} from "../../../../../core/utils/array.utils";

@Component({
  selector: 'app-materialize-plate-exam-static',
  templateUrl: './materialize-plate-exam-static.component.html',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    CardModule,
    ErrorTextModule,
    SelectModule,
    LoadingModule,
    StickyContainerModule,
  ],
  providers: [
    MaterializeAgent,
  ]
})
export class MaterializePlateExamStaticComponent extends BaseView implements AfterViewInit, OnDestroy, OnChanges {
  private static readonly SCALE = 2;
  private static readonly MAX_RECORDING_SECONDS = 15;

  refreshStaticStreamingInProgress = false;
  refreshStaticResultInProgress = false;
  staticStreamingLastFrame: number[][];

  pressureLocation: PressureLocation;
  recordingName: string;
  recordingDuration = 0;
  gravityCenters: PressureGravityCenter[] = [];
  averageGravityCenter: PressureGravityCenter;
  rotation = 0;
  plate: MaterializePlate;
  canvasCellWidth: number;
  canvasCellHeight: number;

  private timerSubscription?: Subscription;
  subscriptions: Subscription[] = [];

  @ViewChild('canvas', { static: true }) canvas!: ElementRef<HTMLCanvasElement>;
  @Input() value: MaterializePlateExamData;
  @Output() dataChangedEvent: EventEmitter<MaterializePlateExamData> = new EventEmitter();

  constructor(public materializeAgent: MaterializeAgent,
              private materializePlateExamControl: MaterializePlateExamControl,
              private cdR: ChangeDetectorRef) {
    super();
  }

  ngAfterViewInit(): void {

    this.subscriptions.push(this.materializeAgent.plateStreamingFrameFound$.subscribe(value => {
      if (value && this.refreshStaticStreamingInProgress === false) {
        if (value.fragments?.length > 0) {
          this.plate = value.fragments[0].plate;
          this.plateChangedOrRotationChanged();
        }
        // console.log('Streaming frame received', value);
        try {
          this.refreshStaticStreamingInProgress = true;
          this.refreshStaticStreaming(value);
        } finally {
          this.refreshStaticStreamingInProgress = false;
        }
      }
    }));

    this.subscriptions.push(this.materializeAgent.staticRecordingResultFound$.subscribe(value => {
      if (value) {
        this.refreshStaticResultInProgress = true;
        this.cdR.detectChanges();
        setTimeout(() => {
          this.plate = value.plate;
          this.refreshStaticResultFromFrames(value.frames);
          this.refreshStaticResultInProgress = false;
          this.cdR.detectChanges();
        }, 500);
      }
    }));
    // console.log('abonnement en place');
    this.subscriptions.push(this.materializeAgent.pressureMeasurementStateChanged$.subscribe(value => {
      // console.log('max', this.recordingName, value, this.recordingName?.toLowerCase() === value?.recordName?.toLowerCase());
      if (this.recordingName && this.recordingName?.toLowerCase() === value?.recordName?.toLowerCase() && value?.state === 'finished') {
        this.refreshStaticResultInProgress = true;
        this.materializeAgent.fetchFrames(this.recordingName)
            .catch((e) => {
              this.refreshStaticResultInProgress = false;
              this.toastControl.presentError(e, 'Récupération du résultat impossible');
            });
      }
    }));

    this.materializeAgent.connect();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['value'] && this.value) {
      this.refreshStaticResult(this.value.pressureData);
    }
  }



  ngOnDestroy() {
    this.stopTimer();
    this.stopAgent();
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  startMaterializeRecording() {
    const context = this.canvas.nativeElement.getContext('2d');
    if (context) {
      context.clearRect(0, 0, this.canvas.nativeElement.width, this.canvas.nativeElement.height);
    }
    this.gravityCenters = [];
    this.averageGravityCenter = undefined;
    this.recordingName = RandomUtils.randomString(30);
    this.startTimer();
    this.pressureLocation = undefined;
    this.materializeAgent.startPlateStaticRecording(this.recordingName, 1)
        .catch((e) => {
          this.startTimer();
          this.toastControl.presentError(e, 'Démarrage de l\'enregistrement plateforme impossible')
        });
  }
  stopMaterializeRecording() {
    this.stopTimer();
    this.pressureLocation = undefined;
    this.stopAgent();
  }

  stopAgent() {
    console.log('stop', this.recordingName);
    if (this.recordingName) {
      this.materializeAgent.stopPlateRecording(this.recordingName)
          .catch((e) => {
            this.refreshStaticResultInProgress = false;
            this.toastControl.presentError(e, 'Arrêt de l\'enregistrement plateforme impossible')
          });
    }
  }

  startTimer() {
    // console.log('start time');
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
    }
    this.recordingDuration = 0;
    this.timerSubscription = interval(1000).subscribe(() => {
      this.recordingDuration++;
      // console.log('timeSubscription', this.staticStreamingLastFrame?.length);
      if (this.staticStreamingLastFrame) {
        this.pressureLocation = this.materializePlateExamControl.computePressureLocation(this.staticStreamingLastFrame);
        // console.log('pressureLocation changed', this.pressureLocation);
      }
      if (this.recordingDuration >= MaterializePlateExamStaticComponent.MAX_RECORDING_SECONDS) {
        this.stopMaterializeRecording();
      }
    });
  }

  stopTimer(): void {
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
    }
  }

  rotationChanged() {
    this.rotation += 90;
    this.plateChangedOrRotationChanged();
    this.gravityCenters = [];
  }

  refreshStaticStreaming(frame: MaterializePlateStreamingFrame) {

    let allFragmentsPressureData: number[][];
    if (frame.fragments?.length > 0) {
      let minX = Infinity;
      let minY = Infinity;
      let maxX = -Infinity;
      let maxY = -Infinity;

      frame.fragments.forEach(({ x, y , width, height}) => {
        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, x + width);
        maxY = Math.max(maxY, y + height);
      });

      // console.log(minX, maxX, minY, maxY);

      // Merge all fragments into one
      allFragmentsPressureData = Array.from({ length: maxX - minX }, () => Array(maxY-minY).fill(0));

      frame.fragments.forEach(fragment => {
        fragment.data = fragment.data[0].map((_, colIndex) => fragment.data.map(row => row[colIndex]));
        for (let x = 0; x < allFragmentsPressureData.length; x++) {
          for (let y = 0; y < allFragmentsPressureData[x].length; y++) {
            const relativeX = x - (fragment.x) + minX;
            const relativeY = y - (fragment.y) + minY;
            if (relativeX > -1 && relativeY > -1 && relativeX < fragment.data.length && relativeY < fragment.data[0].length) {
              allFragmentsPressureData[x][y] = fragment.data[relativeX][relativeY];
            }
          }
        }
      });
    } else {
      allFragmentsPressureData = Array.from({ length: 0}, () => Array(0));
    }
    allFragmentsPressureData = this.materializePlateExamControl.rotateData(allFragmentsPressureData, this.rotation);
    this.staticStreamingLastFrame = allFragmentsPressureData;
    this.refreshCanvas(allFragmentsPressureData, false, false);
  }

  refreshStaticResultFromFrames(frames: MaterializePlateFrame[]){
    const balanced = ArrayUtils.sampleBalanced(frames, 200);
    let average = this.materializePlateExamControl.computeAveragePressureData(
        balanced);
    average = average[0].map((_, colIndex) => average.map(row => row[colIndex]));
    average = this.materializePlateExamControl.trimZeroEdges(average);
    average = this.materializePlateExamControl.rotateData(average, this.rotation);
    this.plateChangedOrRotationChanged();
    this.refreshStaticResult(average);

    this.dataChangedEvent.emit({
      pressureData: average,
      averageGravityCenter: this.averageGravityCenter,
      gravityCenters: this.gravityCenters,
      pressureLocation: this.pressureLocation,
      image: this.canvas?.nativeElement?.toDataURL(),
      plate: this.plate?.summary,
      cellHeight: this.canvasCellHeight,
      cellWidth: this.canvasCellWidth
    } as MaterializePlateExamData);
  }

  refreshStaticResult(pressureData: number[][]) {
    this.refreshCanvas(pressureData, true, true);
  }

  plateChangedOrRotationChanged() {
    if (this.plate) {
      this.canvasCellWidth = this.plate.summary.sensorWidth;
      this.canvasCellHeight = this.plate.summary.sensorHeight;
    } else {
      this.canvasCellWidth = 15;
      this.canvasCellHeight = 10;
    }
    if ((this.rotation % 180) === 0) {
      [this.canvasCellWidth, this.canvasCellHeight] = [this.canvasCellHeight, this.canvasCellWidth];
    }
  }

  refreshCanvas(allFragmentsPressureData: number[][],
                computeQuadrans: boolean,
                isResult: boolean) {
    this.canvas.nativeElement.height = 500;
    this.canvas.nativeElement.width = 500;
    if (allFragmentsPressureData?.length > 0) {

      // Get maxValue
      if (computeQuadrans){
        this.pressureLocation = this.materializePlateExamControl.computePressureLocation(allFragmentsPressureData);
      }

      this.materializePlateExamControl.drawPressureData(this.canvas,
          allFragmentsPressureData,
          this.canvasCellWidth,
          this.canvasCellHeight,
          isResult, true, MaterializePlateExamStaticComponent.SCALE);
      const gravityCenter = this.materializePlateExamControl.computeCenterOfGravity(allFragmentsPressureData);
      if (!isResult) {
        this.gravityCenters.push(gravityCenter);
      } else {
        this.averageGravityCenter = gravityCenter;
      }

      this.materializePlateExamControl.drawGravityCenterAsCross(this.canvas,
          gravityCenter,
          this.canvasCellWidth,
          this.canvasCellHeight,
          MaterializePlateExamStaticComponent.SCALE);

      this.materializePlateExamControl.drawCenterOfGravities(this.canvas?.nativeElement,
          this.gravityCenters,
          allFragmentsPressureData,
          this.canvasCellWidth,
          this.canvasCellHeight,
          MaterializePlateExamStaticComponent.SCALE * 2,
          MaterializePlateExamStaticComponent.SCALE);
    }
  }
}

export class MaterializePlateExamData {
  image: string;
  pressureData: number[][];
  pressureLocation: PressureLocation;
  gravityCenters: PressureGravityCenter[] = [];
  averageGravityCenter: PressureGravityCenter;
  plate: MaterializePlateSummary;
  cellWidth: number;
  cellHeight: number;
}
